import { type FormEvent, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { EXTRA_TERMS } from "../../shared/billing";
import { createRestaurantSchema } from "../../shared/schemas";
import { api } from "../api/client";
import { importApi } from "../api/queries";
import { rememberRestaurant } from "../app/currentRestaurant";
import { useAuth } from "../auth/AuthContext";
import { useAuthOptions } from "../auth/useAuthOptions";
import { FlowSteps } from "../components/FlowSteps";
import { PagePreview, type PagePreviewActions } from "../components/PagePreview";
import { UploadZone } from "../components/UploadZone";
import { Alert, AuthLayout, focusFirstInvalid, TextField } from "../components/ui";
import { importFileError, MAX_IMPORT_FILES } from "../lib/importFiles";
import {
  includedPages,
  isCancelled,
  type PrepSource,
  usePagePreparation,
} from "../lib/prepare/usePagePreparation";
import { ChallengeError, useTurnstile } from "../lib/turnstile";
import { apiFailure, type FieldErrors, validate } from "./forms";

// Regional defaults for the first restaurant: nothing else is asked before the menu is up (spec §1A). All of
// them can be changed later in Setări → Restaurant.
const DEFAULTS = {
  city: "",
  country: "RO",
  defaultLang: "ro",
  currency: "RON",
  timezone: "Europe/Bucharest",
};

/**
 * First access = step 1 "Încarcă" of the fast flow: restaurant name, menu files, languages. The restaurant is
 * created with regional defaults, the import starts, and the user follows it on the Import page, which moves
 * on to "Verifică" by itself. Without a document the user can create the restaurant and add products manually.
 */
export function OnboardingPage({ extra = false }: { extra?: boolean }) {
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [name, setName] = useState("");
  // Chosen documents are prepared in the browser right away (pages, crops, checks), while the form is filled.
  const prep = usePagePreparation();
  // Created by an earlier attempt whose upload failed: a retry reuses it instead of creating a second one.
  const [rid, setRid] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [fileError, setFileError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState<"" | "upload" | "manual">("");
  // Cloudflare Turnstile before the restaurant is created, when configured (invisible unless Cloudflare asks).
  const challenge = useTurnstile(useAuthOptions()?.challengeSiteKey);

  /** Checks chosen files before anything is prepared; false (with the reason shown) when one is refused. */
  function accept(list: File[]): boolean {
    setFileError("");
    if (list.length > MAX_IMPORT_FILES) {
      setFileError(`Poți încărca cel mult ${MAX_IMPORT_FILES} documente o dată.`);
      return false;
    }
    const invalid = list.map(importFileError).find(Boolean);
    if (invalid) setFileError(invalid);
    return !invalid && list.length > 0;
  }

  function pick(list: File[], source: PrepSource) {
    if (accept(list)) void prep.start(list, source);
  }

  const previewActions: PagePreviewActions = {
    onToggle: prep.toggle,
    onRotate: (key) => void prep.rotate(key),
    onMove: prep.move,
    onRemove: prep.remove,
    onRetake: (key, photo, source) => {
      if (accept([photo])) void prep.retake(key, photo, source);
    },
    onAdd: (list, source) => {
      if (accept(list)) void prep.add(list, source);
    },
  };

  /** Validates the name (unless the restaurant already exists); false when the form must be corrected. */
  function checkName(): boolean {
    if (rid) return true;
    const v = validate(createRestaurantSchema, { ...DEFAULTS, name });
    setErrors(v.ok ? {} : v.errors);
    return v.ok;
  }

  async function ensureRestaurant(): Promise<string> {
    if (rid) return rid;
    const r = await api<{ restaurant: { id: string } }>("POST", "/restaurants", {
      ...DEFAULTS,
      name,
      challenge: await challenge.take(),
    });
    setRid(r.restaurant.id);
    rememberRestaurant(r.restaurant.id);
    return r.restaurant.id;
  }

  function fail(err: unknown) {
    const f = apiFailure(err);
    setErrors(f.fields);
    setMessage(
      err instanceof ChallengeError ? err.message : f.message || "Verifică datele și încearcă din nou.",
    );
    setBusy("");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage("");
    const nameOk = checkName();
    const chosen = prep.state.phase !== "idle";
    if (!chosen) setFileError("Alege meniul: un PDF sau fotografii.");
    if (!nameOk || !chosen) {
      focusFirstInvalid(formRef.current);
      return;
    }
    setBusy("upload");
    // The pages may still be in preparation: the click waits for them (no extra step).
    const ready = await prep.whenPrepared();
    if (ready.phase !== "ready" || !includedPages(ready).length) {
      setFileError(
        ready.error || "Nicio pagină nu este inclusă. Include paginile cu meniul din previzualizare.",
      );
      setBusy("");
      return;
    }
    let restaurantId: string | undefined;
    let jobId: string | undefined;
    try {
      restaurantId = await ensureRestaurant();
      const imports = importApi(restaurantId);
      // English printed on the menu is kept as a draft translation; the languages are chosen in the wizard.
      jobId = (await imports.create(["en"])).id;
      await prep.upload(imports, jobId);
      await imports.submit(jobId, ready.ms);
    } catch (err) {
      // A half-uploaded job is cancelled (its files are deleted) so the retry starts cleanly.
      if (restaurantId && jobId)
        await importApi(restaurantId)
          .cancel(jobId)
          .catch(() => undefined);
      if (isCancelled(err)) {
        setBusy("");
        return;
      }
      fail(err);
      return;
    }
    prep.clear();
    // The menu is read in the background while the wizard asks the rest (DEC-107). A further restaurant has no
    // free days to set up: it follows the import, then chooses its plan from „Abonament și facturare”.
    if (extra) {
      window.location.assign("/app/imports");
      return;
    }
    navigate("/app/start?step=2", { replace: true });
    await refresh();
  }

  async function manual() {
    setMessage("");
    if (!checkName()) {
      focusFirstInvalid(formRef.current);
      return;
    }
    setBusy("manual");
    try {
      await ensureRestaurant();
    } catch (err) {
      fail(err);
      return;
    }
    if (extra) {
      window.location.assign("/app/products");
      return;
    }
    navigate("/app/start?step=2", { replace: true });
    await refresh();
  }

  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <span className="eyebrow">{extra ? "Restaurant nou" : "Bun venit"}</span>
        <h1 className="h1">{extra ? "Adaugă un restaurant" : "Publică meniul în câteva minute"}</h1>
        <p className="muted" style={{ margin: 0 }}>
          Încarci meniul pe care îl ai deja. Pregătim produsele și prețurile, tu verifici doar ce e marcat și
          publici.
        </p>
      </div>
      {extra && (
        <Alert tone="info">
          Fiecare restaurant în plus costă {euroText(EXTRA_TERMS.month.priceMinor)} / lună sau{" "}
          {euroText(EXTRA_TERMS.year.priceMinor)} / an + TVA, fără perioadă gratuită. Îl pregătești complet
          acum; meniul se deschide pentru clienți după ce îi alegi abonamentul.
        </Alert>
      )}
      <FlowSteps current={0} />
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <TextField
          id="r-name"
          label="Numele restaurantului"
          autoComplete="organization"
          maxLength={80}
          value={name}
          disabled={!!rid || !!busy}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />
        {/* The first choice (camera, photos, file) until a page is in; then the preview adds the next ones. */}
        {prep.state.phase === "idle" ? (
          <UploadZone
            busy={!!busy}
            busyLabel={busy === "upload" ? "Se încarcă meniul…" : "Se creează restaurantul…"}
            error={fileError}
            onFiles={pick}
          />
        ) : (
          <>
            {fileError && (
              <Alert tone="error" live>
                {fileError}
              </Alert>
            )}
            <PagePreview
              state={prep.state}
              actions={previewActions}
              onCancel={() => (prep.state.phase === "uploading" ? prep.cancel() : prep.clear())}
            />
          </>
        )}
        <div ref={challenge.ref} />
        <p className="caption muted" style={{ margin: 0 }}>
          Limbile, moneda și restul le alegi în pașii următori; poți sări peste oricare.
        </p>
        <button className="btn btn-primary btn-block" type="submit" disabled={!!busy}>
          {busy === "upload"
            ? prep.state.phase === "preparing"
              ? "Pregătim paginile…"
              : "Se încarcă meniul…"
            : "Încarcă meniul"}
        </button>
      </form>
      <p className="auth-foot">
        Nu ai meniul la îndemână?{" "}
        <button type="button" className="link-btn" disabled={!!busy} onClick={() => void manual()}>
          {busy === "manual" ? "Se creează…" : "Adaug produsele manual"}
        </button>
        <br />
        {extra ? (
          <a href="/app">Înapoi la restaurantul tău</a>
        ) : (
          <button type="button" className="link-btn" onClick={() => void logout()}>
            Deconectare
          </button>
        )}
      </p>
    </AuthLayout>
  );
}

const euroText = (minor: number) => `${(minor / 100).toFixed(2).replace(".", ",")} €`;
